import React, { useEffect, useState, useReducer, useContext } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Paper,
  Typography,
  Avatar,
  Chip
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Close } from "@material-ui/icons";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { format, parseISO, isSameDay } from "date-fns";
import { green } from "@material-ui/core/colors";
import clsx from "clsx";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark.png";
import MarkdownWrapper from "../MarkdownWrapper";
import ModalImageCors from "../ModalImageCors";
import LocationPreview from "../LocationPreview";
import VcardPreview from "../VcardPreview";
import AudioModal from "../AudioModal";

import {
  Done,
  DoneAll,
  AccessTime,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      height: "90vh",
      maxHeight: "90vh",
    },
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: theme.spacing(1),
    backgroundColor: theme.palette.total,
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  },
  contactInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  dialogContent: {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  messagesWrapper: {
    backgroundImage: theme.mode === 'light' ? `url(${whatsBackground})` : `url(${whatsBackgroundDark})`,
    backgroundColor: theme.mode === 'light' ? "transparent" : "#0b0b0d",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  messageContainer: {
    display: "flex",
    marginBottom: theme.spacing(1),
  },
  messageLeft: {
    justifyContent: "flex-start",
  },
  messageRight: {
    justifyContent: "flex-end",
  },
  messageBubble: {
    maxWidth: "65%",
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    wordWrap: "break-word",
    position: "relative",
  },
  messageBubbleLeft: {
    backgroundColor: theme.mode === 'light' ? "#ffffff" : "#202c33",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    borderTopLeftRadius: 0,
  },
  messageBubbleRight: {
    backgroundColor: theme.mode === 'light' ? "#dcf8c6" : "#005c4b",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    borderTopRightRadius: 0,
  },
  messageText: {
    fontSize: "14px",
    lineHeight: "19px",
    marginBottom: 4,
  },
  messageTime: {
    fontSize: "11px",
    color: theme.mode === 'light' ? "rgba(0, 0, 0, 0.45)" : "rgba(241, 241, 242, 0.63)",
    display: "flex",
    alignItems: "center",
    gap: 2,
    justifyContent: "flex-end",
  },
  ackIcons: {
    fontSize: "16px",
    marginLeft: 2,
  },
  ackDoneAll: {
    color: green[500],
  },
  divider: {
    margin: theme.spacing(2, 0),
    textAlign: "center",
  },
  dividerText: {
    display: "inline-block",
    padding: theme.spacing(0.5, 2),
    backgroundColor: theme.mode === 'light' ? "#e1f3fb" : "#182229",
    borderRadius: 8,
    fontSize: "12px",
    color: theme.palette.text.secondary,
  },
  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    padding: theme.spacing(1),
    borderRadius: 4,
    marginBottom: 4,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.2)",
    },
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...state, ...newMessages];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const TicketViewOnlyModal = ({ open, onClose, ticketId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { user, socket } = useContext(AuthContext);
  
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !ticketId) {
      dispatch({ type: "RESET" });
      setTicket(null);
      return;
    }

    const fetchTicket = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/tickets/${ticketId}`);
        setTicket(data);

        const { data: messagesData } = await api.get(`/messages/${ticketId}`);
        dispatch({ type: "LOAD_MESSAGES", payload: messagesData.messages });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTicket();
  }, [ticketId, open]);

  useEffect(() => {
    if (!socket || !open || !ticketId) return;

    const onAppMessage = (data) => {
      if (data.action === "create" && data.message.ticketId === ticketId) {
        dispatch({ type: "LOAD_MESSAGES", payload: [data.message] });
      }
    };

    socket.on(`company-${user.companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${user.companyId}-appMessage`, onAppMessage);
    };
  }, [socket, ticketId, open, user.companyId]);

  const renderMessageDivider = (message, index) => {
    if (index === 0) {
      return (
        <div className={classes.divider}>
          <span className={classes.dividerText}>
            {format(parseISO(message.createdAt), "dd/MM/yyyy")}
          </span>
        </div>
      );
    }
    if (index < messagesList.length) {
      const previousMessage = messagesList[index - 1];
      if (!isSameDay(parseISO(previousMessage.createdAt), parseISO(message.createdAt))) {
        return (
          <div className={classes.divider}>
            <span className={classes.dividerText}>
              {format(parseISO(message.createdAt), "dd/MM/yyyy")}
            </span>
          </div>
        );
      }
    }
    return null;
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll className={clsx(classes.ackIcons, classes.ackDoneAll)} />;
    }
    return null;
  };

  const renderQuotedMessage = (message) => {
    if (!message.quotedMsg) return null;

    // Função para limpar o body de templates (remove a parte dos botões JSON)
    const cleanTemplateBody = (body) => {
      if (!body) return body;
      // Se contém o separador ||||, extrai apenas o texto antes dele
      if (body.includes('||||')) {
        return body.split('||||')[0];
      }
      return body;
    };

    return (
      <div
        style={{
          borderLeft: "3px solid #0a0",
          paddingLeft: 8,
          marginBottom: 8,
          backgroundColor: "rgba(0, 0, 0, 0.05)",
          borderRadius: 4,
          padding: 4,
        }}
      >
        <Typography variant="caption" style={{ fontWeight: "bold", display: "block" }}>
          {message.quotedMsg.contact?.name || "Contato"}
        </Typography>
        <Typography variant="caption" style={{ fontSize: "12px" }}>
          {cleanTemplateBody(message.quotedMsg.body)}
        </Typography>
      </div>
    );
  };

  const renderMessageContent = (message) => {
    if (message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    }

    if (message.mediaType === "audio") {
      return <AudioModal url={message.mediaUrl} />;
    }

    if (message.mediaType === "video") {
      return (
        <video
          controls
          style={{ maxWidth: "100%", borderRadius: 8 }}
          src={message.mediaUrl}
        />
      );
    }

    if (message.mediaType === "application" || message.mediaType === "document") {
      return (
        <div className={classes.downloadMedia}>
          <a href={message.mediaUrl} target="_blank" rel="noopener noreferrer">
            📄 {message.body || "Download"}
          </a>
        </div>
      );
    }

    if (message.mediaType === "vcard") {
      return <VcardPreview contact={message.body} />;
    }

    if (message.mediaType === "location" && message.body) {
      const locationData = message.body.split("|");
      return (
        <LocationPreview
          latitude={locationData[0]}
          longitude={locationData[1]}
          name={locationData[2]}
          address={locationData[3]}
        />
      );
    }

    return (
      <MarkdownWrapper>
        {message.body}
      </MarkdownWrapper>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle className={classes.dialogTitle}>
        <div className={classes.contactInfo}>
          <Avatar src={ticket?.contact?.profilePicUrl} />
          <div>
            <Typography variant="h6">{ticket?.contact?.name || "Carregando..."}</Typography>
            <Chip
              size="small"
              label={ticket?.queue?.name || "Sem fila"}
              style={{
                backgroundColor: ticket?.queue?.color || "#999",
                color: "#fff",
              }}
            />
          </div>
        </div>
        <IconButton onClick={onClose} style={{ color: theme.mode === 'light' ? "#303030" : "#ffffff" }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.messagesWrapper}>
          {loading ? (
            <Typography align="center">Carregando mensagens...</Typography>
          ) : (
            messagesList.map((message, index) => (
              <React.Fragment key={message.id}>
                {renderMessageDivider(message, index)}
                <div
                  className={clsx(classes.messageContainer, {
                    [classes.messageLeft]: !message.fromMe,
                    [classes.messageRight]: message.fromMe,
                  })}
                >
                  <Paper
                    className={clsx(classes.messageBubble, {
                      [classes.messageBubbleLeft]: !message.fromMe,
                      [classes.messageBubbleRight]: message.fromMe,
                    })}
                    elevation={1}
                  >
                    {renderQuotedMessage(message)}
                    <div className={classes.messageText}>
                      {renderMessageContent(message)}
                    </div>
                    <div className={classes.messageTime}>
                      {format(parseISO(message.createdAt), "HH:mm")}
                      {message.fromMe && renderMessageAck(message)}
                    </div>
                  </Paper>
                </div>
              </React.Fragment>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TicketViewOnlyModal;
